import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import "../productdetail_v2/ProductDetail.css";
import "../grouppackagedetail/GroupPackageDetail.css";
import {
  Wind,
  ShoppingCart,
  Zap,
  Check,
  Lock,
  ChevronLeft,
  ChevronRight,
  X,
  TriangleAlert,
  Building2,
  CircleSmall,
  ChevronDown,
  Search,
} from "lucide-react";
import Breadcrums from "../../components/breadcrums_v2/Breadcrums";
import AddToCartSuccess from "../../components/addtocart_v2/AddToCartSuccess.js";
import ApiService from "../../components/Api_services";
import MultiPurpose from "../../components/multipurpose_v2/MultiPurpose.js";
import FallbackImg from "../../components/FallbackImg";
import { REDIRECT_LOGIN_URL, SHOW_REGION_POPUP } from "../../constants.js";

const PackageProductDetailv2 = ({ fetchCounts }) => {
  const { item_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [slideIndex, setSlideIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showAddToCart, setShowAddToCart] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derived state
  const product = productData?.product?.[0] || {};
  const pricing = productData?.pricing || {};
  const packagedetail = productData?.product_list || [];
  const features = productData?.features || [];
  const whatsInBox = productData?.whats_in_box || [];

  // Magnifier states
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const magnifierRef = useRef(null);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const isLoggedIn = !!localStorage.getItem("access_token");
  const isBuyNowRef = useRef(false);

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [notice, setNotice] = useState({ show: false, message: "" });
  const [pricing_type, setPricingType] = useState("1");
  const [invalidBrandQty, setInvalidBrandQty] = useState(false);
  const product_code = product?.product;

  // Promotion magnifier states
  const [showPromotionMagnifier, setShowPromotionMagnifier] = useState(false);
  const [promotionMagnifierImg, setPromotionMagnifierImg] = useState("");
  const [promotionMagnifierPosition, setPromotionMagnifierPosition] = useState({
    x: 0,
    y: 0,
  });
  const promotionMagnifierRef = useRef(null);
  const [showPromotionImageModal, setShowPromotionImageModal] = useState(false);
  const [modalPromotionImageUrl, setModalPromotionImageUrl] = useState("");

  const [pendingStep, setPendingStep] = useState("");

  const [branches, setBranches] = useState([]);
  const [searchInputs, setSearchInputs] = useState({});
  const [branchQuantities, setBranchQuantities] = useState({});
  const [branchFocQuantities, setBranchFocQuantities] = useState({});
  const [branchDiscountQuantities, setBranchDiscountQuantities] = useState({});
  const [invalidBranchQty, setInvalidBranchQty] = useState({});
  const [openIndexes, setOpenIndexes] = useState({});
  const [visitedIndexes, setVisitedIndexes] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddToCartSuccess, setShowAddToCartSuccess] = useState({
    show: false,
    tick: false,
    text: "",
  });
  const [distributionMode, setDistributionMode] = useState("hq");
  const [allowAddToCart, setAllowAddToCart] = useState(false);
  const onCartOperationSuccess = null;
  const [calculatedPackagePrice, setCalculatedPackagePrice] = useState({});

  // Region selection state
  const [branchRegions, setBranchRegions] = useState([]);
  const [showRegionPopup, setShowRegionPopup] = useState(false);
  const [tempSelectedRegion, setTempSelectedRegion] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");

  const REGION_LABELS = {
    west_malaysia: "West Malaysia",
    sabah: "Sabah",
    sarawak: "Sarawak",
  };

  // Dummy sub_user permissions – everyone can order/view
  const canOrder = true;
  const canView = true;

  // ── ApiService ──────────────────────────────────
  useEffect(() => {
    if (isLoggedIn) {
      const fetchBranchRegions = async () => {
        try {
          if (SHOW_REGION_POPUP !== 1) return;
          const res = await ApiService.getBranchListWithRegions();
          if (res.regions.length === 1) {
            setSelectedRegion(res.regions[0]);
          } else {
            setBranchRegions(res.regions || []);
            if (res.regions.length > 0) {
              setTempSelectedRegion(res.regions[0]); // default to first region
            }
            setShowRegionPopup(true);
          }
        } catch (error) {
          console.error("Error fetching branch regions:", error);
        }
      };
      fetchBranchRegions();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const fetchPackageDetail = async () => {
      if (!item_id) return;
      if (isLoggedIn && SHOW_REGION_POPUP === 1 && !selectedRegion) return;

      try {
        setLoading(true);
        const response = await ApiService.getPackageDetail(
          item_id,
          SHOW_REGION_POPUP === 1 ? selectedRegion : "",
        );

        if (response && response.status === 200) {
          const statePackageType = location.state?.package_type;
          if (
            response.data.has_flexible_group === true &&
            statePackageType !== "0"
          ) {
            navigate(`/grouppackagedetail/${item_id}`, { replace: true });
            return;
          }
          setProductData(response.data);
        }
      } catch (err) {
        setError("Failed to load product details");
        setProductData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageDetail();
  }, [item_id, selectedRegion]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [item_id]);

  // ── Helpers ──────────────────────────────────
  const formatCurrency = (value) =>
    parseFloat(value).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const sellingPoints = productData?.selling_point || [];

  const getCurrentStock = () => {
    if (
      !productData?.stock_quantity ||
      parseFloat(pricing?.stock_quantity) === 0
    )
      return 0;
    return parseInt(productData?.stock_quantity || 0);
  };

  const isInStock = () => {
    if (
      isLoggedIn &&
      (!productData?.total_brand_price ||
        parseFloat(productData?.total_brand_price) === 0)
    )
      return false;
    return getCurrentStock() > 0;
  };

  const isQuantityValid = () => {
    const stock = getCurrentStock();
    return stock > 0 && quantity <= stock;
  };

  const isItemLimited = () =>
    productData?.item_limit_info?.is_limited || pricing?.is_limited || false;

  // Quantity controls
  function handleDecrease() {
    setQuantity((prev) => {
      const next = prev > stepValue ? prev - stepValue : stepValue;
      return next;
    });
  }

  function handleIncrease() {
    const stock = getCurrentStock();
    setQuantity((prev) => {
      const next =
        stock > 0 && prev + stepValue <= stock ? prev + stepValue : prev;
      return next;
    });
  }

  // Images helper
  const getProductImages = () => {
    const images = [];
    const seen = new Set();
    const push = (type, url, thumb) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      images.push({ type, url, thumb: thumb || url });
    };

    if (productData?.item_video)
      push(
        "video",
        productData.item_video,
        productData.item_photo?.[0]?.thumb_url || productData.photo_url || "",
      );

    if (productData?.photo_url)
      push("image", productData.photo_url, productData.photo_url);

    if (
      Array.isArray(productData?.item_photo) &&
      productData.item_photo.length > 0
    ) {
      productData.item_photo.forEach((p) => {
        const url = p.photo_url || p.image_url || p;
        const thumb = p.thumb_url || p.photo_url || p.image_url || url;
        push("image", url, thumb);
      });
    }

    if (
      Array.isArray(productData?.more_photo) &&
      productData.more_photo.length > 0
    ) {
      productData.more_photo.forEach((p) => {
        const url = p.photo_url || p.image_url || p.url || p;
        const thumb =
          p.thumb_url || p.thumb || p.photo_url || p.image_url || url;
        push("image", url, thumb);
      });
    }

    return images;
  };

  // Fullscreen video mute
  useEffect(() => {
    const handleFullscreenChange = () => {
      const video = videoRef.current;
      if (!video) return;
      const isFullscreen =
        document.fullscreenElement === video ||
        document.webkitFullscreenElement === video;
      video.muted = !isFullscreen;
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  // Magnifier
  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const elem = imageRef.current;
    const { left, top, width, height } = elem.getBoundingClientRect();
    const x = e.pageX - left - window.pageXOffset;
    const y = e.pageY - top - window.pageYOffset;
    const xPercent = Math.max(0, Math.min(100, (x / width) * 100));
    const yPercent = Math.max(0, Math.min(100, (y / height) * 100));
    setMagnifierPosition({ x: xPercent, y: yPercent });
    const lensX = Math.max(25, Math.min(width - 25, x));
    const lensY = Math.max(25, Math.min(height - 25, y));
    setCursorPosition({ x: lensX, y: lensY });
  };

  const handleMouseEnter = () => setShowMagnifier(true);
  const handleMouseLeave = () => setShowMagnifier(false);

  const handlePrevImage = () =>
    setSlideIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  const handleNextImage = () =>
    setSlideIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  const handleImageClick = () => {
    setShowImageModal(true);
    setShowMagnifier(false);
  };
  const handleCloseModal = () => setShowImageModal(false);

  const handlePlayIconClick = (e, idx) => {
    e.stopPropagation();
    setSlideIndex(idx);
    setTimeout(() => {
      const video = document.querySelector(".main-product-video");
      if (video) video.play().catch(() => {});
    }, 100);
  };

  // Quantity init from pricing
  useEffect(() => {
    const qty = parseInt(pricing?.brand_quantity, 10);
    if (qty > 0) setQuantity(qty);
  }, [pricing?.brand_quantity]);

  // Derived state (post-guard)
  const images = getProductImages();
  const notforsell = product?.out_of_stock === "1";
  const isPackageItem = product.package_item === "1";
  const brandQuantity = parseInt(pricing.brand_quantity || 1, 10);
  const stepValue = 1;
  const canBuy = isInStock() && !notforsell;

  function getMaxQuantity() {
    let maxQty = getCurrentStock();
    return maxQty;
  }

  const getDisplayPrice = () => {
    return {
      price: parseFloat(productData?.total_brand_price || 0),
      original: null,
      discount: 0,
    };
  };

  const displayPrice = getDisplayPrice();
  const isDiscontinued = product.status === "2" && !isInStock();

  const handleQuantityInputChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    let numValue = parseInt(value) || 1;
    if (isPackageItem && brandQuantity > 0) {
      setInvalidBrandQty(numValue % brandQuantity !== 0);
    } else {
      setInvalidBrandQty(false);
    }
    const maxQty = getMaxQuantity();
    if (numValue > maxQty) {
      setNotice({
        show: true,
        message: "Sorry, limited items available in stock.",
        yesText: "OK",
      });
      setPendingStep("quantity");
      let finalQty = 1;
      setQuantity(finalQty);
      if (isPackageItem && brandQuantity > 0 && finalQty % brandQuantity !== 0)
        setInvalidBrandQty(true);
      else setInvalidBrandQty(false);
    } else {
      setQuantity(numValue);
    }
  };

  const isOutOfStock = (item, pricingData, loggedIn) => {
    if (!loggedIn) return pricingData.displayPrice === 0;
    return (
      parseInt(item?.stock_quantity || 0) === 0 ||
      pricingData.displayPrice === 0
    );
  };

  // Promotion magnifier
  const handlePromotionMagnifierMouseMove = (e) => {
    if (!promotionMagnifierRef.current) return;
    const elem = promotionMagnifierRef.current;
    const { left, top, width, height } = elem.getBoundingClientRect();
    const x = e.pageX - left - window.pageXOffset;
    const y = e.pageY - top - window.pageYOffset;
    setPromotionMagnifierPosition({
      x: Math.max(0, Math.min(100, (x / width) * 100)),
      y: Math.max(0, Math.min(100, (y / height) * 100)),
    });
  };

  const handlePromotionMagnifierEnter = (imgUrl) => {
    setPromotionMagnifierImg(imgUrl);
    setShowPromotionMagnifier(true);
  };
  const handlePromotionMagnifierLeave = () => setShowPromotionMagnifier(false);
  const handlePromotionImageClick = (imgUrl) => {
    setModalPromotionImageUrl(imgUrl);
    setShowPromotionImageModal(true);
  };

  useEffect(() => {
    const fetchBranches = async () => {
      if (SHOW_REGION_POPUP === 1 && !selectedRegion) return;

      try {
        let branchData;
        const rawBranches = await ApiService.getBranchList({
          region: selectedRegion,
        });
        // Ensure HQ (branch_id === "0") is always first; if absent (region-only list), first element becomes default
        branchData = [
          ...rawBranches.filter((b) => String(b.branch_id) === "0"),
          ...rawBranches.filter((b) => String(b.branch_id) !== "0"),
        ];
        setBranches(branchData);

        // 计算 normalQty
        let groupCount = 0,
          remain = 0,
          normalQty = quantity;
        // 主商品分配
        const initialQuantities = {};
        branchData.forEach((branch, idx) => {
          initialQuantities[branch.branch_id] = idx === 0 ? normalQty : 0;
        });
        setBranchQuantities(initialQuantities);
      } catch (error) {
        alert("Failed to load branches");
      }
    };
    fetchBranches();
  }, [quantity, selectedRegion]);

  const itemProduct = packagedetail.map((spec) => ({
    label: spec.item_code,
    name: spec.item_name,
    item_id: spec.item_id,
    qty: spec.quantity,
    photo_url: spec.item_photo[0]?.photo_url ?? spec.photo_url,
    thumb_url: spec.item_photo[0]?.thumb_url ?? spec.photo_url,
  }));

  const calculateHQQuantityWithPrefix = (
    scaledNormalQty,
    prefix = "",
    explicitHqKey = "",
  ) => {
    if (branches.length === 0) return scaledNormalQty;

    const hqKey = explicitHqKey
      ? explicitHqKey
      : prefix
        ? `${prefix}-${branches[0].branch_id}`
        : branches[0].branch_id;

    const totalAssigned = Object.entries(branchQuantities)
      .filter(([key]) => {
        if (prefix) {
          return key.startsWith(`${prefix}-`) && key !== hqKey;
        }
        const isInnerKey = branches.some((b) =>
          key.endsWith(`-${b.branch_id}`),
        );
        return !isInnerKey && key !== String(branches[0].branch_id);
      })
      .reduce((sum, [, qty]) => sum + qty, 0);

    return scaledNormalQty - totalAssigned;
  };

  const itemDistributionRaw = useMemo(() => {
    return (
      itemProduct?.map((item) => {
        return {
          label: item.label || "",
          name: item.name || null,
          item_id: item.item_id || null,
          qty: (item.qty || 0) * quantity,
          photo_url: item.photo_url || null,
          thumb_url: item.thumb_url || null,
        };
      }) ?? []
    );
  }, [itemProduct, quantity]);

  const toggleOpen = (index) => {
    setOpenIndexes((prev) => ({ ...prev, [index]: !prev[index] }));
    setVisitedIndexes((prev) => new Set(prev).add(index));
  };

  const handleDecreaseWithPrefix = (
    branchId,
    prefix = "",
    explicitHqKey = "",
    shouldReturnToHq = true,
    allowHqAdjustment = false,
  ) => {
    if (branches.length === 0) return;
    const hqBranchId = branches[0].branch_id;
    const key = prefix ? `${prefix}-${branchId}` : branchId;
    const hqKey = explicitHqKey
      ? explicitHqKey
      : prefix
        ? `${prefix}-${hqBranchId}`
        : hqBranchId;

    if (!allowHqAdjustment && (branchId === hqBranchId || key === hqKey))
      return;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      const currentQty = newQuantities[key] || 0;
      if (currentQty >= stepValue) {
        newQuantities[key] = currentQty - stepValue;
        if (shouldReturnToHq) {
          newQuantities[hqKey] = (newQuantities[hqKey] || 0) + stepValue;
        }
      }
      return newQuantities;
    });
  };

  const handleIncreaseWithPrefix = (
    branchId,
    scaledNormalQty,
    prefix = "",
    explicitHqKey = "",
    shouldAutoAdjustHq = true,
    allowHqAdjustment = false,
  ) => {
    if (branches.length === 0) return;
    const hqBranchId = branches[0].branch_id;
    const key = prefix ? `${prefix}-${branchId}` : branchId;
    const hqKey = explicitHqKey
      ? explicitHqKey
      : prefix
        ? `${prefix}-${hqBranchId}`
        : hqBranchId;

    if (!allowHqAdjustment && (branchId === hqBranchId || key === hqKey))
      return;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      const nonHqAssigned = Object.entries(newQuantities)
        .filter(([k]) =>
          prefix ? k.startsWith(`${prefix}-`) && k !== hqKey : k !== hqKey,
        )
        .reduce((sum, [, qty]) => sum + qty, 0);

      const groupAssigned = Object.entries(newQuantities)
        .filter(([k]) => (prefix ? k.startsWith(`${prefix}-`) : true))
        .reduce((sum, [, qty]) => sum + qty, 0);

      const canIncrease = shouldAutoAdjustHq
        ? nonHqAssigned + stepValue <= scaledNormalQty
        : groupAssigned + stepValue <= scaledNormalQty;

      if (canIncrease) {
        newQuantities[key] = (newQuantities[key] || 0) + stepValue;
        if (shouldAutoAdjustHq) {
          newQuantities[hqKey] = scaledNormalQty - (nonHqAssigned + stepValue);
        }
      }
      return newQuantities;
    });
  };

  const allDistTriggersVisited =
    itemDistributionRaw.length > 0
      ? itemDistributionRaw.every(
          (item, index) =>
            item.qty === 0 || visitedIndexes.has(`dist-${index}`),
        )
      : true;

  const allocatedBranchSummary = useMemo(() => {
    if (!Array.isArray(branches) || branches.length === 0) {
      return "N/A";
    }

    const allocatedNames = branches
      .map((branch, branchIdx) => {
        const totalAllocated = itemDistributionRaw.reduce(
          (sum, item, index) => {
            const prefix = `dist-${index}`;
            const qty =
              branchIdx === 0
                ? calculateHQQuantityWithPrefix(item.qty, prefix)
                : branchQuantities[`${prefix}-${branch.branch_id}`] || 0;
            return sum + qty;
          },
          0,
        );

        if (totalAllocated <= 0) return null;
        return (
          branch.branch_name || branch.branch_code || String(branch.branch_id)
        );
      })
      .filter(Boolean);

    if (allocatedNames.length > 0) {
      return allocatedNames.join(", ");
    }

    return (
      branches[0]?.branch_name ||
      branches[0]?.branch_code ||
      String(branches[0]?.branch_id || "N/A")
    );
  }, [branches, branchQuantities, itemDistributionRaw]);

  const summaryItems = useMemo(
    () => packagedetail.filter((item) => Number(item?.quantity || 0) > 0),
    [packagedetail],
  );

  const calculatePackagePrice = async () => {
    try {
      const selections = itemDistributionRaw.map(
        ({ item_id, qty: quantity }) => ({
          item_id,
          quantity,
          group_id: 0,
        }),
      );

      const requestData = {
        package_id: productData?.package_id,
        package_quantity: quantity,
        selections: JSON.stringify(selections),
        mode: "preview",
      };

      const response = await ApiService.calculatePackagePrice(requestData);

      if (response.status === 200) {
        setCalculatedPackagePrice(response);
      }
    } catch (error) {
      console.error("Failed to calculate package price:", error);
    }
  };

  useEffect(() => {
    calculatePackagePrice();
  }, [productData?.package_id, quantity]);

  // const subtotalOriginalPrice = useMemo(() => {
  //   const lineSubtotal = summaryItems.reduce(
  //     (sum, item) =>
  //       sum +
  //       Number(item?.unit_price || 0) * Number(item?.quantity || 0) * quantity,
  //     0,
  //   );
  //   return lineSubtotal;
  // }, [summaryItems, quantity]);

  // const subtotalDiscountAmount = useMemo(() => {
  //   const lineDiscountTotal = summaryItems.reduce(
  //     (sum, item) => {
  //       const lineSubtotal =
  //         Number(item?.unit_price || 0) * Number(item?.quantity || 0) * quantity;
  //       return sum + lineSubtotal * (Number(item?.discount || 0) / 100);
  //     },
  //     0,
  //   );

  //   return lineDiscountTotal;
  // }, [summaryItems, quantity]);

  // const totalAmount = subtotalOriginalPrice - subtotalDiscountAmount;

  const subtotalOriginalPrice = useMemo(() => {
    const selectedGroups = calculatedPackagePrice?.data?.selected_groups || [];
    return selectedGroups.reduce(
      (groupSum, group) => groupSum + Number(group?.original_price || 0),
      0,
    );
  }, [calculatedPackagePrice]);

  const subtotalDiscountAmount = useMemo(() => {
    const selectedGroups = calculatedPackagePrice?.data?.selected_groups || [];
    return selectedGroups.reduce(
      (groupSum, group) => groupSum + Number(group?.discount_amount || 0),
      0,
    );
  }, [calculatedPackagePrice]);

  const handleQuantityInputChangePrefixed = (key, value, scopedNormalQty) => {
    if (!/^\d*$/.test(value)) return;
    const numValue = parseInt(value) || 0;
    const prefix = key.split("-")[0];

    setBranchQuantities((prev) => {
      // 1. Clamp the new step-2 value
      const otherTotal = Object.entries(prev)
        .filter(([k]) => k.startsWith(`${prefix}-`) && k !== key)
        .reduce((sum, [, qty]) => sum + qty, 0);

      const clamped = Math.min(numValue, scopedNormalQty - otherTotal);
      const newQuantities = { ...prev, [key]: clamped };

      // 2. Recompute itemDistributionRaw with the updated quantities
      //    then reset all dist- (step-3) keys back to all-HQ
      const newDistRaw = (itemProduct ?? []).flatMap((item, index) => {
        const step2prefix = String(index);
        const subItems = item.label.split(",").map((s) => s.trim());
        const subItemIds = (item.item_id ?? "").split(",").map((s) => s.trim());
        return subItems.map((subItem, subIndex) => ({
          item_id: subItemIds[subIndex] || null,
          qty: newQuantities[`${step2prefix}-${subItem}`] || 0,
        }));
      });

      newDistRaw.forEach((item, distIndex) => {
        branches.forEach((branch, branchIdx) => {
          newQuantities[`dist-${distIndex}-${branch.branch_id}`] =
            branchIdx === 0 ? item.qty : 0;
        });
      });

      return newQuantities;
    });

    setInvalidBranchQty((prev) => ({ ...prev, [key]: false }));

    // 4. Reset step-3 UI state since allocation was wiped
    setAllowAddToCart(false);
    setDistributionMode("hq");
    setVisitedIndexes(new Set());
  };

  const handleBranchQuantityInputChange = (
    branchId,
    value,
    prefix = "",
    scopedNormalQty = quantity,
  ) => {
    if (branches.length === 0) return;
    const hqBranchId = String(branches[0].branch_id);
    const scopedBranchId = prefix ? `${prefix}-${branchId}` : String(branchId);
    const scopedHqBranchId = prefix ? `${prefix}-${hqBranchId}` : hqBranchId;
    if (scopedBranchId === scopedHqBranchId) return;
    if (!/^\d*$/.test(value)) return;
    let numValue = parseInt(value) || 0;

    setBranchQuantities((prev) => {
      const newQuantities = { ...prev };
      let otherBranchesTotal = 0;
      Object.entries(prev).forEach(([bId, qty]) => {
        const isSameScope = prefix ? bId.startsWith(`${prefix}-`) : true;
        if (isSameScope && bId !== scopedBranchId && bId !== scopedHqBranchId) {
          otherBranchesTotal += qty;
        }
      });
      const maxValue = scopedNormalQty - otherBranchesTotal;
      if (numValue > maxValue) {
        newQuantities[scopedBranchId] = maxValue;
      } else {
        newQuantities[scopedBranchId] = numValue;
      }
      const newDistributedTotal =
        otherBranchesTotal + newQuantities[scopedBranchId];
      newQuantities[scopedHqBranchId] = scopedNormalQty - newDistributedTotal;

      setInvalidBranchQty((prev) => ({ ...prev, [scopedBranchId]: false }));

      return newQuantities;
    });
  };

  const handleAllToHQ = () => {
    setBranchQuantities((prev) => {
      const nextQuantities = { ...prev };

      itemDistributionRaw.forEach((item, index) => {
        const prefix = `dist-${index}`;
        branches.forEach((branch, branchIdx) => {
          const key = `${prefix}-${branch.branch_id}`;
          nextQuantities[key] = branchIdx === 0 ? item.qty : 0;
        });
      });

      return nextQuantities;
    });

    setInvalidBranchQty({});
    setDistributionMode("hq");
    setAllowAddToCart(false);
  };

  const itemIdMap = Object.fromEntries(
    (productData?.groups || []).flatMap((group) =>
      group.items.map((item) => [item.item_code, item.item_id]),
    ),
  );

  const usedCustomerId =
    productData?.pricing?.used_customer_id ||
    productData?.pricing?.bundle_pricing?.used_customer_id ||
    productData?.bundle_items?.[0]?.pricing?.used_customer_id ||
    productData?.customer_id ||
    "";

  const handleAddToCartPackage = async (skipChecks = {}) => {
    if (typeof skipChecks.buyNow === "boolean") {
      isBuyNowRef.current = skipChecks.buyNow;
    }

    if (!isLoggedIn) {
      setShowLoginPrompt(true);
      return;
    }

    try {
      setIsSubmitting(true);

      const cargoDistribution = itemDistributionRaw.map((pkg, pkgIndex) => {
        const prefix = `dist-${pkgIndex}`;

        const distribution = branches
          .map((branch, branchIdx) => {
            const key = `${prefix}-${branch.branch_id}`;
            const qty =
              branchIdx === 0
                ? calculateHQQuantityWithPrefix(pkg.qty, prefix)
                : Number(branchQuantities[key] || 0);

            return { branch_id: branch.branch_id, quantity: qty };
          })
          .filter((d) => d.quantity > 0);

        return {
          item_ref: pkg.item_id,
          distribution,
        };
      });

      const requestData = {
        customer_id: usedCustomerId,
        package_id: productData?.package_id,
        cargo_distribution: JSON.stringify(cargoDistribution),
        pricing_type: productData.pricing_type,
        package_quantity: quantity,
        is_buy_now: isBuyNowRef.current ? "1" : "0",
      };

      // console.log("Package Request Data:", requestData);

      const response = await ApiService.addToCart(requestData);

      if (response.status === 200) {
        if (!isBuyNowRef.current && typeof fetchCounts === "function")
          fetchCounts();
        if (onCartOperationSuccess) {
          onCartOperationSuccess(response.data || response);
        } else if (isBuyNowRef.current) {
          const cartId = Array.isArray(response.results?.id)
            ? response.results.id.join(",")
            : response.results?.id;
          const pricingType = productData.pricing_type || pricing_type;
          if (cartId && pricingType) {
            navigate(`/checkout?pricingtype=${pricingType}&cart_ids=${cartId}`);
          } else {
            setShowAddToCartSuccess({
              show: true,
              tick: false,
              text: "Failed to add item to your shoppping cart, please try again",
            });
          }
        } else {
          setShowAddToCartSuccess({
            show: true,
            tick: true,
            text: "Item has been added to your shopping cart",
          });
        }
      } else {
        setShowAddToCartSuccess({
          show: true,
          tick: false,
          text: "Failed to add item to your shopping cart, please try again",
        });
      }
    } catch (error) {
      console.error("Error with cart operation:", error);
      alert("Error with cart operation: " + (error.message || "Network error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      branches.length === 1 &&
      canBuy &&
      !invalidBrandQty &&
      isQuantityValid()
    ) {
      setAllowAddToCart(true);
    }
  }, [branches.length, quantity]);

  // ── Render ───────────────────────────────────
  return (
    <div className="productdetail">
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          Loading product details...
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
          {error}
        </div>
      ) : !productData ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          Product not found
        </div>
      ) : (
        <>
          <Breadcrums productName={productData.package_description} />

          {showLoginPrompt && (
            <MultiPurpose
              title="Login Required"
              message="You need to login to buy this product."
              yesText="Login"
              noText="Cancel"
              showYesLink={true}
              yesLink={REDIRECT_LOGIN_URL}
              onNo={() => setShowLoginPrompt(false)}
              onClose={() => setShowLoginPrompt(false)}
            />
          )}

          {notice.show && pendingStep === "quantity" && (
            <MultiPurpose
              title="Notice"
              message={notice.message}
              yesText={notice.yesText || "OK"}
              onYes={() => {
                setNotice({ show: false, message: "" });
                setPendingStep("");
              }}
              onClose={() => {
                setNotice({ show: false, message: "" });
                setPendingStep("");
              }}
              hideNo={true}
            />
          )}

          {showAddToCartSuccess.show && (
            <AddToCartSuccess
              text={showAddToCartSuccess.text}
              tick={showAddToCartSuccess.tick}
              onClose={() => {
                setShowAddToCartSuccess({ show: false, text: "" });
              }}
            />
          )}

          <div className="groupproductdetail-content">
            {canView ? (
              <>
                <div className="groupproductdetail-main">
                  <div className="groupproductdetail-left">
                    {/* Step 1 */}
                    <div className="groupproductdetail-card">
                      <div className="groupproductdetail-cardtitle">
                        {allowAddToCart ? (
                          <div className="gpd-num-success">
                            <Check size={16} />
                          </div>
                        ) : (
                          <div className="gpd-num">1</div>
                        )}
                        <div>Select Package Quantity</div>
                      </div>

                      <div className="groupproductdetail-carddetail">
                        <div
                          style={{
                            display: "flex",
                            gap: "28px",
                            width: "100%",
                          }}
                        >
                          {/* LEFT: Images */}
                          <div className="gpd-carddetail-left">
                            <div className="gpd-productimage-container">
                              {images.length > 1 && (
                                <button
                                  className="image-nav-button image-nav-prev"
                                  onClick={handlePrevImage}
                                >
                                  <ChevronLeft size={16} />
                                </button>
                              )}

                              {images[slideIndex]?.type === "video" ? (
                                <div className="video-container">
                                  <video
                                    ref={videoRef}
                                    controls
                                    autoPlay
                                    muted
                                    className="main-product-video"
                                    src={images[slideIndex].url}
                                    poster={
                                      images.find((img) => img.type === "image")
                                        ?.url || undefined
                                    }
                                    key={slideIndex}
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              ) : (
                                <div
                                  className="gpd-image-magnifier-container"
                                  onMouseMove={handleMouseMove}
                                  onMouseEnter={handleMouseEnter}
                                  onMouseLeave={handleMouseLeave}
                                >
                                  {isLoggedIn && !canBuy && !isDiscontinued && (
                                    <div className="product-outofstock-overlay">
                                      Out of stock
                                    </div>
                                  )}
                                  {isDiscontinued && !isInStock() && (
                                    <div className="product-discontinued-overlay">
                                      Discontinued
                                    </div>
                                  )}
                                  <FallbackImg
                                    key={images[slideIndex]?.url}
                                    ref={imageRef}
                                    src={images[slideIndex]?.url}
                                    alt={product.item_name || "Product"}
                                    className="main-product-image"
                                    onClick={handleImageClick}
                                  />
                                  {showMagnifier && (
                                    <div
                                      className="magnifier-lens"
                                      style={{
                                        left: `${cursorPosition.x - 25}px`,
                                        top: `${cursorPosition.y - 25}px`,
                                      }}
                                    />
                                  )}
                                </div>
                              )}

                              {images.length > 1 && (
                                <button
                                  className="image-nav-button image-nav-next"
                                  onClick={handleNextImage}
                                >
                                  <ChevronRight size={16} />
                                </button>
                              )}

                              {showMagnifier &&
                                images[slideIndex]?.type === "image" && (
                                  <div
                                    ref={magnifierRef}
                                    className="magnifier-result"
                                    style={{
                                      backgroundImage: `url(${images[slideIndex].url})`,
                                      backgroundPosition: `${magnifierPosition.x}% ${magnifierPosition.y}%`,
                                    }}
                                  />
                                )}
                            </div>
                          </div>

                          {/* RIGHT: Details */}
                          <div className="gpd-carddetail-right">
                            <div className="gpd-productdetailcode-container">
                              <div className="gpd-productdetailcode">
                                {productData.package_name || "N/A"}
                              </div>
                              <div className="gpd-productdetailname">
                                {productData.package_description ||
                                  "Product Name"}
                              </div>
                            </div>

                            {canBuy ? (
                              <>
                                <div className="gpd-productinfo">
                                  <div className="productprice">
                                    {displayPrice.price > 0 ? (
                                      displayPrice.original ? (
                                        <div className="productprice">
                                          RM{" "}
                                          {formatCurrency(
                                            displayPrice.original,
                                          )}
                                        </div>
                                      ) : (
                                        <div className="productprice">
                                          RM{" "}
                                          {formatCurrency(displayPrice.price)}
                                        </div>
                                      )
                                    ) : null}
                                  </div>

                                  {productData.product_type !== "RCP" && (
                                    <div className="gpd-pricingtypetag">
                                      {productData.product_type || "N/A"}
                                    </div>
                                  )}
                                </div>

                                <div className="gpd-productinput">
                                  <div className="gpd-productqty-container">
                                    <div className="gpd-productqty">
                                      <button
                                        className="gpd-minus-qty-btn"
                                        onClick={handleDecrease}
                                        type="button"
                                        disabled={!isInStock()}
                                      >
                                        −
                                      </button>
                                      <input
                                        type="text"
                                        className="gpd-productqty-value"
                                        value={quantity}
                                        onChange={handleQuantityInputChange}
                                      />
                                      <button
                                        className="gpd-plus-qty-btn"
                                        onClick={handleIncrease}
                                        type="button"
                                        disabled={!isInStock()}
                                      >
                                        +
                                      </button>
                                    </div>
                                    <div
                                      style={{
                                        color: "#737373",
                                        fontSize: "12px",
                                      }}
                                    >
                                      = {quantity} items
                                    </div>
                                  </div>

                                  <div>
                                    {!isQuantityValid() && (
                                      <div className="quantity-error-v2">
                                        Maximum {getMaxQuantity()} items
                                        available
                                      </div>
                                    )}
                                    {invalidBrandQty && (
                                      <div className="quantity-error-v2">
                                        Please enter a quantity that is a
                                        multiple of {brandQuantity}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : null}

                            {!isLoggedIn && (
                              <div style={{ marginTop: "8px" }}>
                                <button
                                  className="pd-button"
                                  onClick={() => {
                                    window.location.href = REDIRECT_LOGIN_URL;
                                  }}
                                >
                                  Login / Register To Buy
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    {branches.length !== 1 && (
                      <div className="groupproductdetail-card">
                        <div className="groupproductdetail-cardtitle">
                          {!canBuy ? (
                            <div className="pd-num-lock">
                              <Lock size={12} />
                            </div>
                          ) : allowAddToCart ? (
                            <div className="gpd-num-success">
                              <Check size={16} />
                            </div>
                          ) : (
                            <div className="gpd-num">2</div>
                          )}
                          <div>Branch Allocation</div>
                        </div>

                        <div
                          className="groupproductdetail-carddetail"
                          style={{ flexDirection: "column" }}
                        >
                          {!canBuy ? (
                            <div className="pd-step2-lock">
                              <div>
                                <Lock size={48} />
                              </div>
                              <div>The item is currently unavailable</div>
                            </div>
                          ) : (
                            <>
                              {/* <div className="gpd-quickactions">
                                <div className="gpd-quickactions-title">
                                  Quick Actions
                                </div>

                                <div className="gpd-quickactions-cardcontainer">
                                  <div
                                    className={`gpd-quickactions-card ${distributionMode === "hq" ? "active" : ""}`}
                                    onClick={handleAllToHQ}
                                  >
                                    <Zap size={16} />{" "}
                                    {branches.length === 1 &&
                                    String(branches[0]?.branch_id) !== "0"
                                      ? `All to ${branches[0]?.branch_code || branches[0]?.branch_name || "Branch"}`
                                      : "All to HQ"}
                                  </div>

                                  <div
                                    className={`gpd-quickactions-card ${distributionMode === "custom" ? "active" : ""} ${branches.length <= 1 ? "disabled" : ""}`}
                                    onClick={() => {
                                      if (branches.length <= 1) return;
                                      setDistributionMode("custom");
                                      setAllowAddToCart(false);
                                    }}
                                    style={{
                                      opacity: branches.length <= 1 ? 0.5 : 1,
                                      cursor:
                                        branches.length <= 1
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                  >
                                    Custom
                                  </div>
                                </div>
                              </div> */}

                              <div className="gpd-dropdown-reminder">
                                Tap an item to open branch allocation. HQ
                                receives the remainder automatically.
                              </div>

                              {itemDistributionRaw.map((item, index) => {
                                if (item.qty === 0) return null;
                                const prefix = `dist-${index}`; // namespaced to avoid collision with itemProduct keys
                                const toggleKey = `dist-${index}`;

                                return (
                                  <div className="gpd-dropdown" key={index}>
                                    {/* Trigger */}
                                    <button
                                      className="gpd-dropdown-trigger"
                                      onClick={() => toggleOpen(toggleKey)}
                                      type="button"
                                    >
                                      <div className="gpd-dropdown-triggeritem-left">
                                        <div className="gpd-dropdown-icon">
                                          <FallbackImg
                                            src={item.thumb_url}
                                            alt={item.label}
                                            width="36"
                                            height="36"
                                            onClick={() =>
                                              handlePromotionImageClick(
                                                item.photo_url,
                                              )
                                            }
                                          />
                                        </div>
                                        <div className="gpd-dropdown-triggeritem">
                                          <div
                                            style={{
                                              color: "#6b7280",
                                              fontSize: "12px",
                                            }}
                                          >
                                            {item.label}
                                          </div>
                                          {visitedIndexes.has(toggleKey) ? (
                                            (() => {
                                              const hqQty =
                                                calculateHQQuantityWithPrefix(
                                                  item.qty,
                                                  prefix,
                                                );
                                              const allocatedCount = branches
                                                .slice(1)
                                                .filter(
                                                  (b) =>
                                                    (branchQuantities[
                                                      `${prefix}-${b.branch_id}`
                                                    ] || 0) > 0,
                                                ).length;
                                              const hqAllocated = hqQty > 0;
                                              if (allocatedCount === 0) {
                                                return (
                                                  <div className="gpd-dropdown-allocation success">
                                                    <Building2
                                                      size={12}
                                                      className="gpd-dropdown-building-icon"
                                                    />
                                                    All to HQ
                                                  </div>
                                                );
                                              }
                                              const label = hqAllocated
                                                ? `HQ and ${allocatedCount} ${allocatedCount === 1 ? "branch" : "branches"}`
                                                : `${allocatedCount} ${allocatedCount === 1 ? "branch" : "branches"}`;
                                              return (
                                                <div className="gpd-dropdown-allocation">
                                                  <CircleSmall
                                                    size={12}
                                                    className="gpd-dropdown-circlesmall-icon"
                                                  />
                                                  {label}
                                                </div>
                                              );
                                            })()
                                          ) : (
                                            <div className="gpd-dropdown-allocation">
                                              <CircleSmall
                                                size={12}
                                                fill={"#d4d4d4"}
                                                color={"#d4d4d4"}
                                              />
                                              Tap to configure
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="gpd-dropdown-triggeritem-right">
                                        <div style={{ color: "#6b7280" }}>
                                          {item.qty}{" "}
                                          {item.qty > 1 ? "units" : "unit"}
                                        </div>

                                        <ChevronDown
                                          size={16}
                                          style={{
                                            transform: openIndexes[toggleKey]
                                              ? "rotate(180deg)"
                                              : "rotate(0deg)",
                                            transition: "transform 0.25s ease",
                                          }}
                                        />
                                      </div>
                                    </button>

                                    {/* Collapsible body */}
                                    <div
                                      style={{
                                        display: openIndexes[toggleKey]
                                          ? "block"
                                          : "none",
                                      }}
                                    >
                                      <div className="atc-branch-searchbar-container">
                                        <div className="atc-branch-searchbar">
                                          <Search size={14} color={"#a1a1a1"} />
                                          <input
                                            placeholder="Search by branch name or code..."
                                            value={
                                              searchInputs[toggleKey] || ""
                                            }
                                            onChange={(e) =>
                                              setSearchInputs((prev) => ({
                                                ...prev,
                                                [toggleKey]: e.target.value,
                                              }))
                                            }
                                          />
                                          <X
                                            className="atc-branch-searchbar-remove"
                                            size={12}
                                            style={{
                                              visibility: searchInputs[
                                                toggleKey
                                              ]
                                                ? "visible"
                                                : "hidden",
                                              pointerEvents: searchInputs[
                                                toggleKey
                                              ]
                                                ? "auto"
                                                : "none",
                                            }}
                                            onClick={() =>
                                              setSearchInputs((prev) => ({
                                                ...prev,
                                                [toggleKey]: "",
                                              }))
                                            }
                                          />
                                        </div>
                                      </div>

                                      <div className="atc-branches">
                                        {(() => {
                                          const filtered = branches.filter(
                                            (branch) =>
                                              !searchInputs[toggleKey] ||
                                              branch.branch_name
                                                ?.toLowerCase()
                                                .includes(
                                                  searchInputs[
                                                    toggleKey
                                                  ].toLowerCase(),
                                                ) ||
                                              branch.branch_code
                                                ?.toLowerCase()
                                                .includes(
                                                  searchInputs[
                                                    toggleKey
                                                  ].toLowerCase(),
                                                ),
                                          );
                                          if (filtered.length === 0) {
                                            return (
                                              <div className="atc-branches-empty">
                                                No branches match "
                                                {searchInputs[toggleKey]}"
                                              </div>
                                            );
                                          }
                                          return filtered.map((branch) => {
                                            const isHQ =
                                              branch.branch_id ===
                                              branches[0]?.branch_id;
                                            const key = `${prefix}-${branch.branch_id}`;
                                            const displayQuantity = isHQ
                                              ? calculateHQQuantityWithPrefix(
                                                  item.qty,
                                                  prefix,
                                                )
                                              : branchQuantities[key] || 0;
                                            return (
                                              <div key={branch.branch_id}>
                                                <div
                                                  className={`atc-branchname-container-v2 ${isHQ && "hq"}`}
                                                >
                                                  <div className="atc-branchname-v2">
                                                    <div className="atc-branch-firstcapital">
                                                      {branch.branch_code
                                                        ?.charAt(0)
                                                        .toUpperCase()}
                                                    </div>

                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                      }}
                                                    >
                                                      <div>
                                                        {branch.branch_code}
                                                      </div>
                                                      {isHQ && (
                                                        <div className="gpd-firstbranch-tag">
                                                          auto remainder
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Quantity 区域 */}
                                                  <div className="atc-productqty-container-v2">
                                                    <div className="atc-productqty-v2">
                                                      <button
                                                        className="atc-productqty-minus-btn-v2"
                                                        onClick={() =>
                                                          handleDecreaseWithPrefix(
                                                            branch.branch_id,
                                                            prefix,
                                                          )
                                                        }
                                                        type="button"
                                                        disabled={
                                                          isHQ ||
                                                          displayQuantity === 0
                                                        }
                                                      >
                                                        −
                                                      </button>
                                                      <input
                                                        type="text"
                                                        className="atc-productqty-value-v2"
                                                        value={displayQuantity}
                                                        onChange={(e) =>
                                                          handleBranchQuantityInputChange(
                                                            branch.branch_id,
                                                            e.target.value,
                                                            prefix,
                                                            item.qty,
                                                          )
                                                        }
                                                        readOnly={isHQ}
                                                      />
                                                      <button
                                                        className="atc-productqty-plus-btn-v2"
                                                        onClick={() =>
                                                          handleIncreaseWithPrefix(
                                                            branch.branch_id,
                                                            item.qty,
                                                            prefix,
                                                          )
                                                        }
                                                        type="button"
                                                        disabled={
                                                          isHQ ||
                                                          calculateHQQuantityWithPrefix(
                                                            item.qty,
                                                            prefix,
                                                          ) === 0
                                                        }
                                                      >
                                                        +
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              {!allDistTriggersVisited && (
                                <div className="gpd-dropdown-reminder-2">
                                  <TriangleAlert size={14} />
                                  Tap each item above to review its allocation
                                  before confirming.
                                </div>
                              )}

                              {!allowAddToCart ? (
                                <div
                                  className={`gpd-button${
                                    !allDistTriggersVisited || invalidBrandQty
                                      ? " disabled"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    !invalidBrandQty &&
                                    (distributionMode !== "custom" ||
                                      allDistTriggersVisited) &&
                                    setAllowAddToCart(true)
                                  }
                                >
                                  Confirm Allocation
                                </div>
                              ) : (
                                <div className="gpd-allocation-confirm">
                                  <Check size={16} /> Allocation confirmed —
                                  ready add to cart
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Package Detail */}
                    <div className="productdetail-card">
                      <div className="productdetail-cardtitle">
                        Package Detail
                      </div>

                      <div className="productdetail-carddetail">
                        <ul className="productdetail-ul">
                          {packagedetail.map((spec, index) => (
                            <li className="productdetail-ulitem" key={index}>
                              {spec.item_code} x {spec.quantity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="groupproductdetail-right">
                    {/* Summary */}
                    <div className="groupproductdetail-card">
                      <div className="groupproductdetail-cardtitle">
                        Order Summary
                      </div>

                      <div
                        className="groupproductdetail-carddetail"
                        style={{ flexDirection: "column" }}
                      >
                        {!canBuy ? (
                          <div className="pd-step2-lock">
                            <div>
                              <Lock size={48} />
                            </div>
                            <div>The item is currently unavailable</div>
                          </div>
                        ) : (
                          <>
                            <div className="gpd-progress">
                              <div className="gpd-progressitem">
                                {allowAddToCart ? (
                                  <div className="gpd-num-success">
                                    <Check size={16} />
                                  </div>
                                ) : (
                                  <div className="gpd-num">1</div>
                                )}
                                <div>
                                  <div>Quantity Selected</div>
                                  <div
                                    style={{
                                      color: "#6b7280",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {quantity} set
                                  </div>
                                </div>
                              </div>

                              <div className="gpd-progressitem">
                                {!canBuy ? (
                                  <div className="pd-num-lock">
                                    <Lock size={12} />
                                  </div>
                                ) : allowAddToCart ? (
                                  <div className="gpd-num-success">
                                    <Check size={16} />
                                  </div>
                                ) : (
                                  <div className="gpd-num">2</div>
                                )}
                                <div>
                                  <div>Branch Allocation</div>
                                  <div
                                    style={{
                                      color: "#6b7280",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {!canBuy ? (
                                      <>Not Configured</>
                                    ) : (
                                      <>{allocatedBranchSummary}</>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="gpd-separator"></div>

                            {summaryItems.length > 0 && (
                              <>
                                <div className="gpd-summary">
                                  <div className="gpd-summary-title">
                                    Selected Item
                                  </div>
                                  <div className="gpd-summaryitem-box-container">
                                    {summaryItems.map((item) => (
                                      <div
                                        className="gpd-summaryitem-box"
                                        key={item.item_id}
                                      >
                                        <div className="gpd-summaryitem-left">
                                          <FallbackImg
                                            src={item.item_photo[0].thumb_url}
                                            alt={item.item_name}
                                            width="56"
                                            height="56"
                                            onClick={() =>
                                              handlePromotionImageClick(
                                                item.item_photo[0].photo_url,
                                              )
                                            }
                                            style={{ cursor: "pointer" }}
                                          />
                                        </div>

                                        <div style={{ flex: "1" }}>
                                          <div className="gpd-summaryitem-righttop">
                                            <div className="gpd-summaryitem-itemcode">
                                              {item.item_code}
                                            </div>
                                            <div className="gpd-summaryitem-itemname">
                                              {item.item_name}
                                            </div>
                                          </div>

                                          <div className="gpd-summaryitem-rightbot">
                                            <div className="gpd-summaryitem-info-1">
                                              <span>Unit price</span>
                                              <span>
                                                x {item.quantity * quantity}{" "}
                                                {(item.quantity * quantity)
                                                  .length > 0
                                                  ? "units"
                                                  : "unit"}
                                              </span>
                                            </div>

                                            <div className="gpd-summaryitem-info-2">
                                              <span
                                                style={{ color: "#6b7280" }}
                                              >
                                                RM{" "}
                                                {formatCurrency(
                                                  item.unit_price,
                                                )}
                                              </span>

                                              <span style={{ display: "flex" }}>
                                                RM
                                                <div className="gpd-summaryitem-price">
                                                  {formatCurrency(
                                                    Number(
                                                      item.unit_price || 0,
                                                    ) *
                                                      Number(
                                                        item.quantity || 0,
                                                      ) *
                                                      quantity,
                                                  )}
                                                </div>
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="gpd-separator"></div>
                              </>
                            )}

                            <div className="gpd-summaryitem-container">
                              <span style={{ color: "#6b7280" }}>Subtotal</span>
                              <span style={{ display: "flex" }}>
                                RM
                                <div className="gpd-price">
                                  {formatCurrency(subtotalOriginalPrice)}
                                </div>
                              </span>
                            </div>
                            {subtotalDiscountAmount > 0 && (
                              <div className="gpd-summaryitem-container">
                                <span style={{ color: "#6b7280" }}>
                                  Discount
                                </span>
                                <span
                                  style={{
                                    color: "#00a63e",
                                    display: "flex",
                                  }}
                                >
                                  - RM
                                  <div className="gpd-price">
                                    {formatCurrency(subtotalDiscountAmount)}
                                  </div>
                                </span>
                              </div>
                            )}

                            <div className="gpd-summaryitem-totalprice-container">
                              <span>Total Payable</span>
                              <span
                                className="gpd-totalprice"
                                style={{ display: "flex", fontSize: "18px" }}
                              >
                                RM
                                <div className="gpd-price">
                                  {formatCurrency(
                                    calculatedPackagePrice?.data?.final_price,
                                  )}
                                </div>
                              </span>
                            </div>

                            <div className="gpd-separator"></div>

                            {allowAddToCart ? (
                              <div style={{ display: "flex", gap: "8px" }}>
                                <div
                                  className="gpd-button"
                                  onClick={() =>
                                    handleAddToCartPackage({ buyNow: false })
                                  }
                                  style={{ flex: "1" }}
                                >
                                  {isSubmitting ? (
                                    "Processing..."
                                  ) : (
                                    <>
                                      <ShoppingCart size={16} /> Add To Cart
                                    </>
                                  )}
                                </div>
                                <div
                                  className="gpd-button"
                                  onClick={() =>
                                    handleAddToCartPackage({ buyNow: true })
                                  }
                                  style={{
                                    flexShrink: 0,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {isSubmitting ? (
                                    "Processing..."
                                  ) : (
                                    <>
                                      <ShoppingCart size={16} /> Buy Now
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="gpd-reminder">
                                Complete branch allocation to add to cart
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Modal */}
                {showImageModal && images[slideIndex]?.type === "image" && (
                  <div className="image-modal" onClick={handleCloseModal}>
                    <div
                      className="image-modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="modal-close"
                        onClick={handleCloseModal}
                      >
                        <X size={16} />
                      </button>
                      {images.length > 1 && (
                        <button
                          className="modal-nav-prev"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrevImage();
                          }}
                        >
                          <ChevronLeft size={16} />
                        </button>
                      )}
                      <FallbackImg
                        key={images[slideIndex]?.url}
                        src={images[slideIndex]?.url}
                        alt={product.item_name || "Product"}
                        className="modal-image"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {images.length > 1 && (
                        <button
                          className="modal-nav-next"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNextImage();
                          }}
                        >
                          <ChevronRight size={16} />
                        </button>
                      )}
                      {images.length > 1 && (
                        <div className="modal-image-counter">
                          {slideIndex + 1} / {images.length}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Image Modal - promotion */}
                {showPromotionImageModal && (
                  <div
                    className="image-modal"
                    onClick={() => setShowPromotionImageModal(false)}
                  >
                    <div
                      className="image-modal-content"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="modal-close"
                        onClick={() => setShowPromotionImageModal(false)}
                      >
                        <X size={16} />
                      </button>
                      <FallbackImg
                        src={modalPromotionImageUrl}
                        alt="Promotion"
                        className="modal-image"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  minHeight: "60vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ color: "#b71c1c", fontWeight: 600 }}>
                  You do not have permission to view this section.
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {showRegionPopup && (
        <MultiPurpose
          title="Select Order Region"
          yesText="Continue"
          noText="Cancel"
          onYes={() => {
            setSelectedRegion(tempSelectedRegion);
            setShowRegionPopup(false);
          }}
          onNo={() => {
            setShowRegionPopup(false);
            navigate("/productlist");
          }}
          onClose={() => {
            setShowRegionPopup(false);
            navigate("/productlist");
          }}
        >
          <div>
            <div style={{ textAlign: "left", marginBottom: "24px" }}>
              Please select a region to continue order:
            </div>
            {branchRegions.map((region) => (
              <label
                key={region}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "10px",
                  cursor: "pointer",
                  fontWeight: tempSelectedRegion === region ? "600" : "400",
                }}
              >
                <input
                  type="radio"
                  name="region"
                  value={region}
                  checked={tempSelectedRegion === region}
                  onChange={() => setTempSelectedRegion(region)}
                />
                {REGION_LABELS[region] || region}
              </label>
            ))}
          </div>
        </MultiPurpose>
      )}
    </div>
  );
};

export default PackageProductDetailv2;
